import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';
import { CaseData, GameState, PendingEvent, VerdictType } from './core/types';
import {
  createInitialState,
  applyVerdict,
  getVerdictOutcome,
  processDueEvents,
  isDayComplete,
  advanceDay,
  getGameOverReason,
  GameOverReason,
} from './core/engine';
import { localizeCase } from './core/localize';
import { getCases, pickRandomCase, getEnergy, saveEnergy, getProgress, saveProgress, saveScore } from './data/api';
import { ENERGY_MAX } from './core/energy';
import { supabase } from './lib/supabase';
import type { Session } from '@supabase/supabase-js';
import TopBar from './components/TopBar';
import ApplicantPanel from './components/ApplicantPanel';
import TestimonyPanel from './components/TestimonyPanel';
import DocumentsSection from './components/DocumentsSection';
import DecisionPanel from './components/DecisionPanel';
import ResultModal from './components/ResultModal';
import QuestionPanel from './components/QuestionPanel';
import Timeline from './components/Timeline';
import DayEndScreen from './components/DayEndScreen';
import GameOverScreen from './components/GameOverScreen';
import PerspectiveSelect, { Perspective } from './components/PerspectiveSelect';
import LoginScreen from './components/LoginScreen';
import LeaderboardPage from './pages/LeaderboardPage';
import AuthCallbackPage from './pages/AuthCallbackPage';

const LANG_KEY = 'borderjudge-lang';
const SAVE_KEY = 'borderjudge-save';

interface VerdictResult {
  verdict: VerdictType;
  firedEvents: PendingEvent[];
}

interface DayStats {
  day: number;
  processed: number;
  quota: number;
  kpi: number;
  careerRisk: number;
  proceduralRisk: number;
}

interface SaveData {
  gameState: GameState;
  perspective: Perspective;
  currentCaseId: string | null;
  usedCaseIds: string[];
}

function loadLocalSave(): SaveData | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      gameState: parsed.gameState ?? createInitialState(),
      perspective: parsed.perspective ?? null,
      currentCaseId: parsed.currentCaseId ?? null,
      usedCaseIds: parsed.usedCaseIds ?? [],
    };
  } catch (err) {
    console.warn('Failed to load local save:', err);
    return null;
  }
}

function App() {
  const { t, i18n } = useTranslation();
  const saved = React.useMemo(() => loadLocalSave(), []);
  
  const [gameState, setGameState] = useState<GameState>(saved?.gameState ?? createInitialState());
  const [cases, setCases] = useState<CaseData[]>([]);
  const [currentCase, setCurrentCase] = useState<CaseData | null>(null);
  const [currentCaseId, setCurrentCaseId] = useState<string | null>(saved?.currentCaseId ?? null);
  const [usedCaseIds, setUsedCaseIds] = useState<string[]>(saved?.usedCaseIds ?? []);
  
  const [verdictResult, setVerdictResult] = useState<VerdictResult | null>(null);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [dayEndStats, setDayEndStats] = useState<DayStats | null>(null);
  const [gameOver, setGameOver] = useState(false);
  const [gameOverReason, setGameOverReason] = useState<GameOverReason | null>(null);
  const [perspective, setPerspective] = useState<Perspective | null>(saved?.perspective ?? null);
  const [authenticated, setAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [energy, setEnergy] = useState(ENERGY_MAX);
  const [initError, setInitError] = useState<string | null>(null);
  
  const userIdRef = React.useRef<string | null>(null);

  // Core persistence: save state to localStorage whenever important things change
  const persistState = React.useCallback((gs: GameState, cid: string | null, uids: string[]) => {
    if (perspective) {
      localStorage.setItem(SAVE_KEY, JSON.stringify({ 
        gameState: gs, 
        perspective, 
        currentCaseId: cid, 
        usedCaseIds: uids 
      }));
    }
  }, [perspective]);

  useEffect(() => {
    persistState(gameState, currentCaseId, usedCaseIds);
  }, [gameState, currentCaseId, usedCaseIds, persistState]);

  // Sync to Cloud
  async function syncProgressToCloud(cid: string | null, used: string[]) {
    if (userIdRef.current) {
      await saveProgress(userIdRef.current, { currentCaseId: cid, usedCaseIds: used });
    }
  }

  function pickNextCaseId(pool: CaseData[], currentUsed: string[]): string | 'COMPLETE' {
    const unused = pool.filter(c => !currentUsed.includes(c.id));
    if (unused.length === 0) return 'COMPLETE';
    
    const picked = pickRandomCase(unused);
    return picked.id;
  }

  async function loadEnergy(uid: string) {
    try {
      const { energy: current } = await getEnergy(uid);
      setEnergy(current);
    } catch (err) {
      console.error('Failed to load energy:', err);
    }
  }

  function syncOAuthProfile(session: Session) {
    if (session.user.is_anonymous) return;
    const meta = session.user.user_metadata ?? {};
    const nickname = meta.full_name ?? meta.name ?? meta.user_name ?? session.user.email;
    if (!nickname) return;
    const avatarUrl = meta.avatar_url ?? meta.picture ?? null;
    supabase.from('profiles').upsert(
      { user_id: session.user.id, nickname, avatar_url: avatarUrl },
      { onConflict: 'user_id' }
    );
  }

  useEffect(() => {
    async function syncLanguageFromDB(userId: string) {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('language')
          .eq('user_id', userId)
          .maybeSingle();
        if (data?.language) {
          i18n.changeLanguage(data.language);
          localStorage.setItem(LANG_KEY, data.language);
        }
      } catch (err) {
        console.warn('Language sync failed:', err);
      }
    }

    async function initialize() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setAuthenticated(true);
          userIdRef.current = session.user.id;
          syncOAuthProfile(session);
          syncLanguageFromDB(session.user.id);
          loadEnergy(session.user.id);
          
          const cloud = await getProgress(session.user.id);
          if (cloud.usedCaseIds.length > usedCaseIds.length) {
            setUsedCaseIds(cloud.usedCaseIds);
            setCurrentCaseId(cloud.currentCaseId);
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        setAuthChecked(true);
      }

      try {
        const loaded = await getCases();
        setCases(loaded);
        
        if (loaded.length === 0) {
          setInitError('No cases found in database. Please run seed data.');
          return;
        }

        // TERMINAL STATE CHECK (Priority 1: Game Over)
        const reason = getGameOverReason(gameState);
        if (reason) {
          setGameOverReason(reason);
          setGameOver(true);
          return;
        }

        // TERMINAL STATE CHECK (Priority 2: All Cases Done)
        if (usedCaseIds.length >= loaded.length && !currentCaseId) {
          setGameOverReason('all_cases_completed');
          setGameOver(true);
          return;
        }

        // TERMINAL STATE CHECK (Priority 3: Day Complete)
        if (isDayComplete(gameState)) {
          setDayEndStats({
            day: gameState.currentDay,
            processed: gameState.processedToday,
            quota: gameState.dailyQuota,
            kpi: gameState.kpi,
            careerRisk: gameState.careerRisk,
            proceduralRisk: gameState.proceduralRisk,
          });
        } else {
          // Normal Flow: Load case
          let targetId = currentCaseId;
          if (!targetId) {
            const next = pickNextCaseId(loaded, usedCaseIds);
            if (next === 'COMPLETE') {
              setGameOverReason('all_cases_completed');
              setGameOver(true);
              return;
            }
            targetId = next;
            setCurrentCaseId(targetId);
            setUsedCaseIds(prev => {
              const updated = [...prev, next];
              syncProgressToCloud(targetId, updated);
              return updated;
            });
          }
          const found = loaded.find(c => c.id === targetId) ?? loaded[0];
          setCurrentCase(found);
        }
      } catch (err) {
        console.error('Case loading error:', err);
        setInitError('Failed to load cases. Check connection or console.');
      }
    }

    initialize();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setAuthenticated(true);
        userIdRef.current = session.user.id;
        syncOAuthProfile(session);
      }
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerdict = (verdict: VerdictType) => {
    if (!currentCase) return;
    setButtonsDisabled(true);

    const afterVerdict = applyVerdict(gameState, verdict, currentCase);
    const [afterEvents, firedEvents] = processDueEvents(afterVerdict);

    setGameState(afterEvents);
    setVerdictResult({ verdict, firedEvents });

    // IMMEDIATELY update local storage to prevent refresh bypass
    persistState(afterEvents, currentCaseId, usedCaseIds);

    // IMMEDIATE SAVE: If this verdict triggered a terminal state, save it NOW.
    const reason = getGameOverReason(afterEvents);
    const dayDone = isDayComplete(afterEvents);

    if (reason || dayDone) {
      if (userIdRef.current) {
        saveScore(userIdRef.current, afterEvents.currentDay, {
          kpi: afterEvents.kpi,
          careerRisk: afterEvents.careerRisk,
          proceduralRisk: afterEvents.proceduralRisk,
        }, !!reason);
      }
    }
  };

  const handleNext = () => {
    setVerdictResult(null);
    setButtonsDisabled(false);

    const reason = getGameOverReason(gameState);
    if (reason) {
      setGameOverReason(reason);
      setGameOver(true);
      return;
    }

    if (isDayComplete(gameState)) {
      setCurrentCaseId(null);
      setCurrentCase(null);
      syncProgressToCloud(null, usedCaseIds);
      setDayEndStats({
        day: gameState.currentDay,
        processed: gameState.processedToday,
        quota: gameState.dailyQuota,
        kpi: gameState.kpi,
        careerRisk: gameState.careerRisk,
        proceduralRisk: gameState.proceduralRisk,
      });
      return;
    }

    const nextId = pickNextCaseId(cases, usedCaseIds);
    if (nextId === 'COMPLETE') {
      setGameOverReason('all_cases_completed');
      setGameOver(true);
      if (userIdRef.current) {
        saveScore(userIdRef.current, gameState.currentDay, {
          kpi: gameState.kpi,
          careerRisk: gameState.careerRisk,
          proceduralRisk: gameState.proceduralRisk,
        }, false);
      }
      return;
    }
    
    setCurrentCaseId(nextId);
    setUsedCaseIds(prev => {
      const updated = [...prev, nextId];
      syncProgressToCloud(nextId, updated);
      return updated;
    });
    setCurrentCase(cases.find(c => c.id === nextId)!);
  };

  const handleNextDay = async () => {
    setDayEndStats(null);
    const advanced = advanceDay(gameState);
    setGameState(advanced);
    
    const nextId = pickNextCaseId(cases, usedCaseIds);
    if (nextId === 'COMPLETE') {
      setGameOverReason('all_cases_completed');
      setGameOver(true);
      if (userIdRef.current) {
        saveScore(userIdRef.current, advanced.currentDay, {
          kpi: advanced.kpi,
          careerRisk: advanced.careerRisk,
          proceduralRisk: advanced.proceduralRisk,
        }, false);
      }
      return;
    }

    setCurrentCaseId(nextId);
    setUsedCaseIds(prev => {
      const updated = [...prev, nextId];
      syncProgressToCloud(nextId, updated);
      return updated;
    });
    setCurrentCase(cases.find(c => c.id === nextId)!);

    setEnergy(prev => {
      const updated = Math.max(0, prev - 1);
      if (userIdRef.current) {
        saveEnergy(userIdRef.current, updated);
      }
      return updated;
    });
  };

  const handleShareBonus = () => {
    const newEnergy = Math.min(energy + 1, ENERGY_MAX);
    setEnergy(newEnergy);
    if (userIdRef.current) saveEnergy(userIdRef.current, newEnergy);
  };

  if (window.location.pathname === '/auth/callback') return <AuthCallbackPage />;
  if (window.location.search.includes('leaderboard')) return <LeaderboardPage />;

  if (process.env.NODE_ENV === 'development') {
    const preview = new URLSearchParams(window.location.search).get('preview');
    if (preview === 'gameover_kpi')
      return <GameOverScreen gameState={{ ...createInitialState(), kpi: 0, currentDay: 3, totalProcessed: 12 }} reason="kpi_zero" onRestart={() => window.location.search = ''} />;
    if (preview === 'gameover_career')
      return <GameOverScreen gameState={{ ...createInitialState(), careerRisk: 100, currentDay: 5, totalProcessed: 20 }} reason="career_risk" onRestart={() => window.location.search = ''} />;
    if (preview === 'gameover_proc')
      return <GameOverScreen gameState={{ ...createInitialState(), proceduralRisk: 100, currentDay: 4, totalProcessed: 18 }} reason="procedural_risk" onRestart={() => window.location.search = ''} />;
    if (preview === 'victory')
      return <GameOverScreen gameState={{ ...createInitialState(), currentDay: 7, totalProcessed: 30 }} reason="all_cases_completed" onRestart={() => window.location.search = ''} />;
  }

  if (initError) return <div className="loading error">Error: {initError}</div>;
  if (!authChecked) return <div className="loading">{t('loading')}</div>;
  if (!authenticated) return <LoginScreen onAuthenticated={() => setAuthenticated(true)} />;
  if (!perspective) return <PerspectiveSelect onSelect={setPerspective} />;

  if (gameOver && gameOverReason) {
    return (
      <GameOverScreen
        gameState={gameState}
        reason={gameOverReason}
        onRestart={async () => {
          localStorage.removeItem(SAVE_KEY);
          if (userIdRef.current) {
            await saveProgress(userIdRef.current, { currentCaseId: null, usedCaseIds: [] });
          }
          window.location.reload();
        }}
      />
    );
  }

  const localizedCase = currentCase ? localizeCase(currentCase, i18n.language) : null;

  return (
    <div className="game-container">
      <TopBar gameState={gameState} energy={energy} />
      {localizedCase ? (
        <>
          <div className="main-content">
            <ApplicantPanel applicant={localizedCase.applicant} caseId={currentCase!.id} />
            <TestimonyPanel testimony={localizedCase.testimony} />
          </div>
          <DocumentsSection documents={localizedCase.documents} issues={localizedCase.issues} />
          <Timeline caseData={localizedCase} />
          <QuestionPanel questions={localizedCase.questions} />
          <DecisionPanel
            disabled={buttonsDisabled}
            onVerdict={handleVerdict}
          />
          {verdictResult && (
            <ResultModal
              verdict={verdictResult.verdict}
              outcome={getVerdictOutcome(currentCase!, verdictResult.verdict)}
              caseData={localizedCase}
              firedEvents={verdictResult.firedEvents}
              onNext={handleNext}
            />
          )}
        </>
      ) : (
        <div className="loading">{t('loading')}</div>
      )}
      {dayEndStats && (
        <DayEndScreen stats={dayEndStats} energy={energy} onNextDay={handleNextDay} onShareBonus={handleShareBonus} />
      )}
    </div>
  );
}

export default App;
